<script setup lang="ts">
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";

const config = useRuntimeConfig();

//retrieve css colors from API and also logos urls
const { data } = await useFetch<CssColors>("/api/css-colors");
const { data: aboutMeData } = await useFetch<AboutMeData>("/api/about-me");
const { data: slider } = await useFetch<SliderData>("/api/sliders");

useState<SliderData>("slider-data", () => slider.value!);
useState<Record<CssColorKeys, string>>("css-colors", () => mapCssColorsResponse(data.value!));
useState<string>("logo-url", () => `${config.public.metApiBaseUrl}${data.value!.logo.url}`);
useState<string>("logo-footer-url", () => `${config.public.metApiBaseUrl}${data.value!.logoFooter.url}`);

useState<AboutMeData>("about-me-data", () => aboutMeData.value!);

const cssColors = useState<Record<CssColorKeys, string>>("css-colors");

const siteTitle = "Met Devoti – Contemporary Marble Sculptor";

useSeoMeta({
  title: siteTitle,
  ogTitle: siteTitle,
  description:
    "Met Devoti is a Carrara-born sculptor who combines Carrara marble, light and sound to create visionary contemporary installations where space, time and matter vibrate in harmony.",
  ogDescription:
    "Discover the work of Met Devoti, a sculptor from Carrara whose marble installations blend tradition, music and experimentation, transforming space into a unique artistic experience.",
  ogImage: "https://api.metdevoti.com/uploads/logo_footer_332b17c6e6.png",
  ogType: "website",
  twitterCard: "summary_large_image",
  twitterTitle: siteTitle,
  twitterDescription:
    "Met Devoti is a contemporary sculptor working with Carrara marble, light and sound to create timeless, immersive installations.",
});

useHead({
  titleTemplate: (titleChunk) => {
    return titleChunk != siteTitle ? `${titleChunk} - ${siteTitle}` : siteTitle;
  },
  link: [
    {
      rel: "stylesheet",
      href: "/css/style.css",
    },
    {
      rel: "stylesheet",
      href: "/css/elements.css",
    },
    {
      rel: "stylesheet",
      href: "/css/responsive.css",
    },
    {
      rel: "stylesheet",
      href: "/css/custom.css", // I file in public sono accessibili dalla root
    },
  ],
  script: [
    {
      src: "/js/plugins.js",
      defer: true,
    },
    {
      //src: "/js/main.js",
      defer: false,
    },
  ],
});

// useScript("https://open.spotify.com/embed/iframe-api/v1");
// console.log("CSS COLORS", cssColors.value);
const ready = ref(false);

onMounted(async () => {
  document.documentElement.style.setProperty("--background-color", cssColors?.value?.[CssColorKeys.HTML_BACKGROUND]!);
  document.documentElement.style.setProperty("--title-color", cssColors?.value?.[CssColorKeys.TITLE_TEXT]!);
  document.documentElement.style.setProperty("--primary-color", cssColors?.value?.[CssColorKeys.PRIMARY_COLOR]!);
  document.documentElement.style.setProperty("--paragraph-color", cssColors?.value?.[CssColorKeys.PARAGRAPH_TEXT]!);
  document.documentElement.style.setProperty("--subtitle-color", cssColors?.value?.[CssColorKeys.SUBTITLE_TEXT]!);
  await nextTick();
  setTimeout(() => {
    ready.value = true;
  }, 300);
  // Inizializza i plugin jQuery solo lato client
  await nextTick();
  if (import.meta.client) {
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother);
    await nextTick();
    // @ts-ignore
    window.$ = window.jQuery = (await import("jquery")).default;
    setTimeout(() => {
      // const smoother = ScrollSmoother.create({
      //   wrapper: "#smooth-wrapper",
      //   content: "#smooth-content",
      //   smooth: true,
      //   effects: true,
      // });
    }, 300);
  }
});
</script>
<template>
  <div id="smooth-wrapper" style="overflow: hidden;">
    <!-- <div id="smooth-content"> -->
    <!-- Fine loading globale personalizzato -->
    <!-- NuxtLoadingIndicator per navigazioni -->
    <NuxtLoadingIndicator :height="4" :duration="2000" color="var(--primary-color)">
      <template #default>
        <Loader :is-loading="true"></Loader>
      </template>
    </NuxtLoadingIndicator>

    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <!-- </div> -->
  </div>
</template>

<style scoped>
.global-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  color: white;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
<style>
html, body {
  background-color: var(--background-color);
}
#lightboxOverlay {
  opacity: 1 !important;
}

.masonry-item {
  margin-bottom: 1px !important;
  padding: 0 1px !important;
}

.xboot-standard-row.dark-row {
  background-color: var(--background-color);
}
</style>
